import React, {useEffect, useMemo, useRef, useState} from 'react';

import PageBG from './components/PageBG';

// Enhanced Mobile Controls Component with proper touch handling
// This component should replace MobileControlsInline in your FrozzlersOnline (22).jsx file

function MobileControlsEnhanced(props) {
  const {
    ONLINE, socket, lobbyId,
    gameState, phase, currentPlayer, currentPlayerConfig,
    placementPos, setPlacementPos, setPhase, setAim, handleShot, clampToSector,
    ARENA, myIndex, currentSectorGeom, playerColor
  } = props;

  const [angle, setAngle] = React.useState(0);
  const [power, setPower] = React.useState(50);
  const [isDragging, setIsDragging] = React.useState(false);
  const powerSliderRef = React.useRef(null);
  const controlsRef = React.useRef(null);

  // Reset when turn changes
  React.useEffect(() => {
    if (phase === "place") {
      setPower(50);
      setAngle(0);
      setIsDragging(false);
    }
  }, [phase, currentPlayer]);

  const show = gameState === "playing" && !currentPlayerConfig?.isAI;
  const disabled = ONLINE && myIndex !== currentPlayer;
  const canInteract = show && !disabled;

  // Get default placement
  const getDefaultPlacement = React.useCallback(() => {
    const sg = currentSectorGeom();
    const midR = (sg.innerR + sg.outerR) * 0.5;
    return { 
      x: ARENA.cx + Math.cos(sg.centerAngle) * midR, 
      y: ARENA.cy + Math.sin(sg.centerAngle) * midR 
    };
  }, [ARENA, currentSectorGeom]);

  // Handle puck down action
 const onPuckDown = React.useCallback((e) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  
  if (!canInteract || phase !== "place") return;
  
  // Use default placement if no position set yet
  const finalPos = getDefaultPlacement();
  setPlacementPos(finalPos);
  
  // Transition to aim phase immediately (no setTimeout)
  setPhase("aim");
  
  // Calculate initial angle towards center
  const centerAngle = Math.atan2(ARENA.cy - finalPos.y, ARENA.cx - finalPos.x);
  setAngle(centerAngle);
  
  // Set initial aim
  const dist = 80 + power * 1.2;
  const curr = { 
    x: finalPos.x + Math.cos(centerAngle) * dist, 
    y: finalPos.y + Math.sin(centerAngle) * dist 
  };
  setAim({ start: { ...finalPos }, curr });
}, [canInteract, phase, placementPos, getDefaultPlacement, ARENA, power, setPlacementPos, setPhase, setAim]);
  
  const nudgeDeg = React.useCallback((deg) => {
    if (!canInteract || phase !== "aim" || !placementPos) return;
    
    const rad = (deg * Math.PI) / 180;
    const newAngle = angle + rad;
    setAngle(newAngle);
    
    const dist = 80 + power * 1.2;
    const curr = { 
      x: placementPos.x + Math.cos(newAngle) * dist, 
      y: placementPos.y + Math.sin(newAngle) * dist 
    };
    setAim({ start: { ...placementPos }, curr });
  }, [canInteract, phase, placementPos, angle, power, setAim]);

  // Handle power change
  const onPowerChange = React.useCallback((newPower) => {
    if (!canInteract || phase !== "aim" || !placementPos) return;
    
    const clamped = Math.max(0, Math.min(100, newPower));
    setPower(clamped);
    
    const dist = 80 + clamped * 1.2;
    const curr = { 
      x: placementPos.x + Math.cos(angle) * dist, 
      y: placementPos.y + Math.sin(angle) * dist 
    };
    setAim({ start: { ...placementPos }, curr });
  }, [canInteract, phase, placementPos, angle, setAim]);

  // Touch handlers for power slider
  const updatePowerFromTouch = React.useCallback((touch) => {
    if (!powerSliderRef.current) return;
    const rect = powerSliderRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    onPowerChange(percentage * 100);
  }, [onPowerChange]);

  const handlePowerTouchStart = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canInteract || phase !== "aim") return;
    setIsDragging(true);
    if (e.touches && e.touches[0]) {
      updatePowerFromTouch(e.touches[0]);
    }
  }, [canInteract, phase, updatePowerFromTouch]);

  const handlePowerTouchMove = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging || !canInteract || phase !== "aim") return;
    if (e.touches && e.touches[0]) {
      updatePowerFromTouch(e.touches[0]);
    }
  }, [isDragging, canInteract, phase, updatePowerFromTouch]);

  const handlePowerTouchEnd = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  // Handle shoot action
  const onShoot = React.useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!canInteract || phase !== "aim" || !placementPos) return;
    
    const MAX_DRAG = 200, SCALE = 0.06;
    const pct = Math.max(0, Math.min(1, power / 100));
    const speed = MAX_DRAG * SCALE * pct;
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;

    if (ONLINE && socket) {
      socket.emit('gameAction', { lobbyId, action: 'shoot', payload: { velocity: { vx, vy } } });
      setAim(null);
    } else {
      handleShot({ vx, vy });
    }
  }, [canInteract, phase, placementPos, power, angle, ONLINE, socket, lobbyId, setAim, handleShot]);

  if (!show) return null;

  const currentColor = playerColor ? playerColor(currentPlayer) : "#2563eb";

  const styles = {
    wrap: {
      position: "fixed",
      left: 0,
      right: 0,
      bottom: 0,
      height: "auto",
      pointerEvents: "none",
      zIndex: 1000,
      touchAction: "none"
    },
    dock: {
      padding: "16px",
      display: "flex",
      gap: "12px",
      justifyContent: "center",
      alignItems: "center",
      background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 40%)",
      pointerEvents: "auto",
      touchAction: "none"
    },
    btn: {
      padding: "12px 16px",
      borderRadius: 12,
      border: "2px solid rgba(255,255,255,0.2)",
      background: "rgba(20,20,20,0.9)",
      color: "white",
      fontWeight: 600,
      fontSize: "16px",
      minWidth: "50px",
      touchAction: "none",
      WebkitTapHighlightColor: "transparent",
      userSelect: "none",
      cursor: "pointer",
      outline: "none"
    },
    primary: {
      padding: "14px 20px",
      borderRadius: 12,
      background: `linear-gradient(135deg, ${currentColor} 0%, ${currentColor}dd 100%)`,
      border: "none",
      color: "white",
      fontWeight: 700,
      fontSize: "16px",
      touchAction: "none",
      WebkitTapHighlightColor: "transparent",
      userSelect: "none",
      cursor: "pointer",
      boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
      outline: "none"
    },
    sliderContainer: {
      position: "relative",
      width: 180,
      height: 40,
      display: "flex",
      alignItems: "center",
      touchAction: "none",
      cursor: "pointer"
    },
    sliderTrack: {
      width: "100%",
      height: 10,
      borderRadius: 5,
      background: "rgba(255,255,255,0.2)",
      position: "relative",
      overflow: "hidden"
    },
    sliderFill: {
      height: "100%",
      background: `linear-gradient(90deg, ${currentColor} 0%, ${currentColor}dd 100%)`,
      borderRadius: 5,
      transition: isDragging ? "none" : "width 0.1s"
    },
    sliderThumb: {
      position: "absolute",
      width: 28,
      height: 28,
      borderRadius: "50%",
      background: "white",
      border: `3px solid ${currentColor}`,
      top: "50%",
      transform: "translate(-50%, -50%)",
      transition: isDragging ? "none" : "left 0.1s",
      boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
      pointerEvents: "none"
    },
    powerLabel: {
      position: "absolute",
      top: -22,
      left: "50%",
      transform: "translateX(-50%)",
      color: "white",
      fontSize: "13px",
      fontWeight: 600,
      textShadow: "0 1px 3px rgba(0,0,0,0.5)"
    }
  };

  return (
    <div ref={controlsRef} style={styles.wrap} aria-hidden={disabled ? "true" : "false"}>
      <div style={styles.dock}>
        {phase === "place" ? (
          <button 
            style={styles.primary} 
            onTouchEnd={(e) => {
              e.preventDefault();
              onPuckDown(e);
            }}
            onClick={onPuckDown}
            disabled={!canInteract}
          >
            PUCK DOWN
          </button>
        ) : phase === "aim" ? (
          <>
            <button 
              style={styles.btn} 
              onTouchEnd={(e) => {
                e.preventDefault();
                nudgeDeg(-5);
              }}
              onClick={() => nudgeDeg(-5)}
              disabled={!canInteract}
            >
              ←
            </button>
            
            <div 
              ref={powerSliderRef}
              style={styles.sliderContainer}
              onTouchStart={handlePowerTouchStart}
              onTouchMove={handlePowerTouchMove}
              onTouchEnd={handlePowerTouchEnd}
            >
              <div style={styles.powerLabel}>Power: {Math.round(power)}%</div>
              <div style={styles.sliderTrack}>
                <div style={{...styles.sliderFill, width: `${power}%`}} />
                <div style={{...styles.sliderThumb, left: `${power}%`}} />
              </div>
            </div>
            
            <button 
              style={styles.btn} 
              onTouchEnd={(e) => {
                e.preventDefault();
                nudgeDeg(5);
              }}
              onClick={() => nudgeDeg(5)}
              disabled={!canInteract}
            >
              →
            </button>
            
            <button 
              style={styles.primary} 
              onTouchEnd={(e) => {
                e.preventDefault();
                onShoot(e);
              }}
              onClick={onShoot}
              disabled={!canInteract}
            >
              SHOOT
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
import { useOutletContext, useParams, useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { saveGameResult, getUserProfile } from './lib/supabase';
// === Mobile Viewport & Gesture Helpers (non-destructive) ===
const _vpClamp = (v, min, max) => Math.max(min, Math.min(max, v));
function vpScreenToWorld(vp, sx, sy) { return { x: (sx - vp.tx) / vp.scale, y: (sy - vp.ty) / vp.scale }; }
function vpWorldToScreen(vp, x, y) { return { x: x * vp.scale + vp.tx, y: y * vp.scale + vp.ty }; }
function vpApplyPinch(vp, scaleDelta, cx, cy) {
  const pre = vpScreenToWorld(vp, cx, cy);
  vp.scale = _vpClamp(vp.scale * scaleDelta, 0.4, 2.5);
  const post = vpWorldToScreen(vp, pre.x, pre.y);
  vp.tx += (cx - post.x);
  vp.ty += (cy - post.y);
}


// Frozzlers – Enhanced with AI Players and Move Timer
// Features: Guest mode integration, AI opponents, move timer, custom player colors, database integration

const W = 820; // canvas width
const H = 820; // canvas height
const ARENA = { cx: W / 2, cy: H / 2, r: 300 };
const RINGS = [
  { r: 60, score: 10, color: "#f87171" },
  { r: 150, score: 5, color: "#60a5fa" },
  { r: 240, score: 3, color: "#34d399" },
  { r: 300, score: 1, color: "#fbbf24" },
];

const PUCK_R = 14;
const ROCK_R_MIN = 12;
const ROCK_R_MAX = 26;
const WALL_RESTITUTION = 0.9;
const PUCK_RESTITUTION = 0.92;
const ROCK_RESTITUTION = 0.85;
const FRICTION = 0.995; // per frame multiplier (low friction)
const STOP_SPEED = 0.06; // below this, we consider puck at rest

const CENTER_DOT_R = 4; // visual & scoring radius for center dot touch bonus

// Launch sectors (8) – randomized geometry per game
const N_SECTORS = 8;
const OUTER_R = RINGS[RINGS.length - 1].r;
const FIXED_ZONE_OFFSET = 50; // radial offset outside the outer ring
const FIXED_ZONE_THICKNESS = 50;
const FIXED_SECTOR_WIDTH_FACTOR = 1.8; // × base width

// Move timer settings
const MOVE_TIME_LIMIT = 15; // seconds per move
const TIMER_INCREMENT = 0.25; // quarter-second increments
const AI_THINKING_TIME = { // AI thinking delays in milliseconds
  'Basic': 1000,
  'iThinker': 2000,
  'Angry Teenager': 500,
  'PhD Scientist': 3000,
  'Insane': 800
};

function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function distance(x1, y1, x2, y2) { return Math.hypot(x2 - x1, y2 - y1); }
function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
function angleOf(x, y) { return Math.atan2(y - ARENA.cy, x - ARENA.cx); }
function normAngle(a) { while (a <= -Math.PI) a += Math.PI * 2; while (a > Math.PI) a -= Math.PI * 2; return a; }

function randomRocks(seed, count) {
  const rnd = mulberry32(seed);
  const rocks = [];
  for (let i = 0; i < count; i++) {
    const rr = ROCK_R_MIN + rnd() * (ROCK_R_MAX - ROCK_R_MIN);
    const ang = rnd() * Math.PI * 2;
    const rad = (RINGS[RINGS.length - 1].r - 30) * Math.sqrt(rnd());
    const x = ARENA.cx + Math.cos(ang) * rad;
    const y = ARENA.cy + Math.sin(ang) * rad;
    rocks.push({ x, y, r: rr });
  }
  return rocks;
}

function computeScore(x, y) {
  const d = distance(x, y, ARENA.cx, ARENA.cy);
  // Center dot touch bonus
  if (d <= PUCK_R + CENTER_DOT_R) return 15;
  for (let i = 0; i < RINGS.length; i++) if (d <= RINGS[i].r) return RINGS[i].score;
  return 0;
}

// Enhanced AI Decision Making Functions - Progressive Intelligence System
const AIPlayer = {
  // Physics-based trajectory calculation for precise shots
  calculateOptimalVelocity: (fromPos, targetPos, difficulty, rng) => {
    const dx = targetPos.x - fromPos.x;
    const dy = targetPos.y - fromPos.y;
    const distance = Math.hypot(dx, dy);
    
    // Balanced physics calculation for all difficulty levels
    // Base velocity calculation that works for all distances
    let baseVelocity = distance * 0.045; // Base scaling factor
    
    // Add minimum velocity to ensure shots can reach target
    baseVelocity = Math.max(baseVelocity, 3.0);
    
    // Cap maximum velocity to prevent overshooting
    baseVelocity = Math.min(baseVelocity, 15);
    
    // Normalize direction
    const dirX = dx / distance;
    const dirY = dy / distance;
    
    // Apply difficulty-based accuracy (affects precision, not power)
    let accuracyMultiplier, angleError, powerError;
    
    switch (difficulty) {
      case 'Insane':
        accuracyMultiplier = 0.975; // 97.5% accuracy
        angleError = (rng() - 0.5) * 0.015; // ±0.86 degrees
        powerError = 0.98 + (rng() * 0.04); // ±2% power variance
        break;
      case 'PhD Scientist':
        accuracyMultiplier = 0.90; // 90% accuracy
        angleError = (rng() - 0.5) * 0.035; // ±2.0 degrees
        powerError = 0.95 + (rng() * 0.10); // ±5% power variance
        break;
      case 'Angry Teenager':
        accuracyMultiplier = 0.75; // 75% accuracy
        angleError = (rng() - 0.5) * 0.06; // ±3.4 degrees
        powerError = 0.90 + (rng() * 0.20); // ±10% power variance
        break;
      case 'iThinker':
        accuracyMultiplier = 0.60; // 60% accuracy
        angleError = (rng() - 0.5) * 0.10; // ±5.7 degrees
        powerError = 0.85 + (rng() * 0.30); // ±15% power variance
        break;
      case 'Basic':
      default:
        accuracyMultiplier = 0.40; // 40% accuracy
        angleError = (rng() - 0.5) * 0.17; // ±9.7 degrees
        powerError = 0.75 + (rng() * 0.50); // ±25% power variance
        break;
    }
    
    // Apply angle error
    const perfectAngle = Math.atan2(dy, dx);
    const actualAngle = perfectAngle + angleError;
    const finalDirX = Math.cos(actualAngle);
    const finalDirY = Math.sin(actualAngle);
    
    // Apply power error (but not accuracy multiplier to power)
    const finalSpeed = baseVelocity * powerError;
    
    return {
      vx: finalDirX * finalSpeed,
      vy: finalDirY * finalSpeed,
      accuracy: accuracyMultiplier
    };
  },

  // Advanced game state analysis with strategic insights
  analyzeGameState: (existingPucks, currentPlayer, difficulty) => {
    const myPucks = existingPucks.filter(p => p.owner === currentPlayer);
    const opponentPucks = existingPucks.filter(p => p.owner !== currentPlayer);
    
    // Score all pucks with detailed analysis
    const myScore = myPucks.reduce((sum, p) => sum + computeScore(p.x, p.y), 0);
    const opponentScore = opponentPucks.reduce((sum, p) => sum + computeScore(p.x, p.y), 0);
    
    // Analyze opponent threats by value and position
    const opponentThreats = opponentPucks
      .map(p => ({
        puck: p,
        score: computeScore(p.x, p.y),
        distanceFromCenter: distance(p.x, p.y, ARENA.cx, ARENA.cy),
        threat: computeScore(p.x, p.y) / Math.max(1, distance(p.x, p.y, ARENA.cx, ARENA.cy) / 50)
      }))
      .sort((a, b) => b.threat - a.threat);
    
    // Find strategic opportunities
    const centerDistance = 60; // Inner ring radius
    const centerArea = existingPucks.filter(p => 
      distance(p.x, p.y, ARENA.cx, ARENA.cy) <= centerDistance
    );
    
    const myBestPuck = myPucks.reduce((best, p) => {
      const score = computeScore(p.x, p.y);
      return score > computeScore(best?.x || 0, best?.y || 0) ? p : best;
    }, null);
    
    const opponentBestPuck = opponentPucks.reduce((best, p) => {
      const score = computeScore(p.x, p.y);
      return score > computeScore(best?.x || 0, best?.y || 0) ? p : best;
    }, null);
    
    // Calculate center accessibility
    const centerBlocked = centerArea.length > 6;
    const centerDominance = centerArea.filter(p => p.owner === currentPlayer).length - 
                           centerArea.filter(p => p.owner !== currentPlayer).length;
    
    return {
      myScore,
      opponentScore,
      scoreDifference: myScore - opponentScore,
      isWinning: myScore > opponentScore,
      opponentThreats,
      centerArea,
      centerBlocked,
      centerDominance,
      myBestPuck,
      opponentBestPuck,
      gamePhase: existingPucks.length < 6 ? 'early' : existingPucks.length < 12 ? 'mid' : 'late'
    };
  },

  // Intelligent target selection based on game state and difficulty
  selectOptimalTarget: (gameState, difficulty, rng, fromPos) => {
    const { 
      scoreDifference, 
      isWinning, 
      opponentThreats, 
      centerBlocked, 
      centerDominance,
      gamePhase 
    } = gameState;
    
    let targetPos, strategy, priority;
    
    // Advanced strategy selection based on difficulty
    switch (difficulty) {
      case 'Insane':
        // Unpredictable but highly effective decision making
        if (gamePhase === 'late' && scoreDifference < -10) {
          // Desperate situation - go for chaos or high-risk high-reward
          if (rng() < 0.4) {
            strategy = 'chaos';
            targetPos = { x: ARENA.cx + (rng() - 0.5) * 40, y: ARENA.cy + (rng() - 0.5) * 40 };
          } else {
            strategy = 'center_snipe';
            targetPos = { x: ARENA.cx + (rng() - 0.5) * 8, y: ARENA.cy + (rng() - 0.5) * 8 };
          }
          priority = 'high_risk';
        } else if (opponentThreats.length > 0 && opponentThreats[0].score >= 10) {
          // Surgical strike on high-value targets
          const target = opponentThreats[0].puck;
          const knockAngle = Math.atan2(target.y - ARENA.cy, target.x - ARENA.cx);
          const knockDistance = 120 + rng() * 60;
          targetPos = {
            x: target.x + Math.cos(knockAngle) * knockDistance,
            y: target.y + Math.sin(knockAngle) * knockDistance
          };
          strategy = 'precision_strike';
          priority = 'eliminate_threat';
        } else if (!centerBlocked && rng() < 0.6) {
          // Perfect center dot targeting
          targetPos = { x: ARENA.cx + (rng() - 0.5) * 6, y: ARENA.cy + (rng() - 0.5) * 6 };
          strategy = 'center_perfect';
          priority = 'maximum_points';
        } else {
          // Unpredictable tactical shot
          const angle = rng() * Math.PI * 2;
          const radius = 30 + rng() * 50;
          targetPos = {
            x: ARENA.cx + Math.cos(angle) * radius,
            y: ARENA.cy + Math.sin(angle) * radius
          };
          strategy = 'tactical_chaos';
          priority = 'unpredictable';
        }
        break;
        
      case 'PhD Scientist':
        // Highly analytical and strategic
        if (isWinning && scoreDifference > 20) {
          // Large lead - play defensively
          const safeAngle = rng() * Math.PI * 2;
          const safeRadius = 40 + rng() * 30;
          targetPos = {
            x: ARENA.cx + Math.cos(safeAngle) * safeRadius,
            y: ARENA.cy + Math.sin(safeAngle) * safeRadius
          };
          strategy = 'defensive_safe';
          priority = 'maintain_lead';
        } else if (opponentThreats.length > 0 && opponentThreats[0].score >= 5) {
          // Calculated opponent elimination
          const target = opponentThreats[0].puck;
          const optimalKnockAngle = Math.atan2(target.y - ARENA.cy, target.x - ARENA.cx);
          const knockDistance = 100;
          targetPos = {
            x: target.x + Math.cos(optimalKnockAngle) * knockDistance,
            y: target.y + Math.sin(optimalKnockAngle) * knockDistance
          };
          strategy = 'calculated_strike';
          priority = 'threat_elimination';
        } else if (centerDominance >= 0 && !centerBlocked) {
          // Optimal center placement
          targetPos = { x: ARENA.cx + (rng() - 0.5) * 12, y: ARENA.cy + (rng() - 0.5) * 12 };
          strategy = 'optimal_center';
          priority = 'score_maximization';
        } else {
          // Strategic positioning
          const strategicAngle = rng() * Math.PI * 2;
          const strategicRadius = 35 + rng() * 40;
          targetPos = {
            x: ARENA.cx + Math.cos(strategicAngle) * strategicRadius,
            y: ARENA.cy + Math.sin(strategicAngle) * strategicRadius
          };
          strategy = 'strategic_position';
          priority = 'board_control';
        }
        break;
        
      case 'Angry Teenager':
        // Aggressive with good power but less strategic depth
        if (opponentThreats.length > 0 && rng() < 0.7) {
          // Aggressive knockout attempts
          const target = opponentThreats[0].puck;
          const aggressiveAngle = Math.atan2(target.y - ARENA.cy, target.x - ARENA.cx);
          const aggressiveDistance = 80 + rng() * 80;
          targetPos = {
            x: target.x + Math.cos(aggressiveAngle) * aggressiveDistance,
            y: target.y + Math.sin(aggressiveAngle) * aggressiveDistance
          };
          strategy = 'aggressive_knockout';
          priority = 'attack';
        } else {
          // Power shot to center
          targetPos = { x: ARENA.cx + (rng() - 0.5) * 25, y: ARENA.cy + (rng() - 0.5) * 25 };
          strategy = 'power_center';
          priority = 'aggressive_score';
        }
        break;
        
      case 'iThinker':
        // Decent strategy but inconsistent execution
        if (opponentThreats.length > 0 && opponentThreats[0].score >= 8 && rng() < 0.5) {
          // Sometimes targets high-value opponents
          const target = opponentThreats[0].puck;
          targetPos = {
            x: target.x + (rng() - 0.5) * 60,
            y: target.y + (rng() - 0.5) * 60
          };
          strategy = 'basic_knockout';
          priority = 'opponent_target';
        } else {
          // Usually aims for center area
          targetPos = { x: ARENA.cx + (rng() - 0.5) * 35, y: ARENA.cy + (rng() - 0.5) * 35 };
          strategy = 'center_focus';
          priority = 'center_points';
        }
        break;
        
      case 'Basic':
      default:
        // Simple center targeting with high variance
        targetPos = { x: ARENA.cx + (rng() - 0.5) * 80, y: ARENA.cy + (rng() - 0.5) * 80 };
        strategy = 'basic_center';
        priority = 'simple_score';
        break;
    }
    
    return { targetPos, strategy, priority };
  },

  // Enhanced obstacle avoidance with physics prediction
  findOptimalPath: (fromPos, targetPos, rocks, existingPucks, difficulty) => {
    const directPath = { x: targetPos.x - fromPos.x, y: targetPos.y - fromPos.y };
    const directDist = Math.hypot(directPath.x, directPath.y);
    
    // Advanced path analysis for higher difficulties
    if (difficulty === 'Basic' || difficulty === 'iThinker') {
      return directPath; // Simple direct path
    }
    
    // Check for obstacles with collision prediction
    const obstacles = [...rocks, ...existingPucks];
    const pathClearance = [];
    
    for (const obstacle of obstacles) {
      const obstacleR = obstacle.r || PUCK_R;
      const safetyMargin = difficulty === 'Insane' ? 5 : difficulty === 'PhD Scientist' ? 8 : 12;
      
      // Line-circle intersection test
      const toObstacle = { x: obstacle.x - fromPos.x, y: obstacle.y - fromPos.y };
      const projection = Math.max(0, Math.min(1, 
        (toObstacle.x * directPath.x + toObstacle.y * directPath.y) / (directDist * directDist)
      ));
      
      const closestPoint = {
        x: fromPos.x + projection * directPath.x,
        y: fromPos.y + projection * directPath.y
      };
      
      const distToPath = distance(obstacle.x, obstacle.y, closestPoint.x, closestPoint.y);
      const clearance = distToPath - (obstacleR + PUCK_R + safetyMargin);
      
      pathClearance.push({ obstacle, clearance, projection });
    }
    
    // Find the most problematic obstacle
    const blockers = pathClearance.filter(p => p.clearance < 0 && p.projection > 0.1 && p.projection < 0.9);
    
    if (blockers.length === 0) {
      return directPath; // Clear path
    }
    
    // Calculate avoidance path for advanced difficulties
    const primaryBlocker = blockers.reduce((worst, current) => 
      current.clearance < worst.clearance ? current : worst
    );
    
    const avoidanceAngle = difficulty === 'Insane' ? Math.PI / 6 : Math.PI / 4; // 30° or 45°
    const baseAngle = Math.atan2(directPath.y, directPath.x);
    
    // Try both avoidance directions and pick the better one
    const avoidanceOptions = [
      {
        x: Math.cos(baseAngle + avoidanceAngle) * directDist * 0.95,
        y: Math.sin(baseAngle + avoidanceAngle) * directDist * 0.95
      },
      {
        x: Math.cos(baseAngle - avoidanceAngle) * directDist * 0.95,
        y: Math.sin(baseAngle - avoidanceAngle) * directDist * 0.95
      }
    ];
    
    // Choose the path with better clearance
    return avoidanceOptions[Math.floor(Math.random() * 2)];
  },

  // Get optimal placement position within sector
  getPlacement: (difficulty, sectorGeom, existingPucks, rocks, rng) => {
    const { centerAngle, innerR, outerR } = sectorGeom;
    
    // Base position calculation
    let targetRadius, angleOffset;
    
    switch (difficulty) {
      case 'Insane':
        // Optimal positioning with micro-adjustments
        targetRadius = innerR + (outerR - innerR) * (0.3 + rng() * 0.4); // 30-70% through zone
        angleOffset = (rng() - 0.5) * 0.1; // ±2.9 degrees
        break;
      case 'PhD Scientist':
        // Strategic positioning
        targetRadius = innerR + (outerR - innerR) * (0.25 + rng() * 0.5); // 25-75% through zone
        angleOffset = (rng() - 0.5) * 0.15; // ±4.3 degrees
        break;
      case 'Angry Teenager':
        // Aggressive forward positioning
        targetRadius = innerR + (outerR - innerR) * (0.1 + rng() * 0.4); // 10-50% through zone (closer)
        angleOffset = (rng() - 0.5) * 0.2; // ±5.7 degrees
        break;
      case 'iThinker':
        // Decent positioning with some variance
        targetRadius = innerR + (outerR - innerR) * (0.2 + rng() * 0.6); // 20-80% through zone
        angleOffset = (rng() - 0.5) * 0.25; // ±7.2 degrees
        break;
      case 'Basic':
      default:
        // Random positioning within sector
        targetRadius = innerR + (outerR - innerR) * rng(); // Full range
        angleOffset = (rng() - 0.5) * 0.4; // ±11.5 degrees
        break;
    }
    
    const finalAngle = centerAngle + angleOffset;
    const x = ARENA.cx + Math.cos(finalAngle) * targetRadius;
    const y = ARENA.cy + Math.sin(finalAngle) * targetRadius;
    
    return { x, y };
  },

  // Main AI decision function
  makeMove: (difficulty, sectorGeom, existingPucks, rocks, currentPlayer, rng) => {
    // Get optimal placement position
    const fromPos = AIPlayer.getPlacement(difficulty, sectorGeom, existingPucks, rocks, rng);
    
    // Analyze current game state
    const gameState = AIPlayer.analyzeGameState(existingPucks, currentPlayer, difficulty);
    
    // Select optimal target
    const { targetPos, strategy, priority } = AIPlayer.selectOptimalTarget(gameState, difficulty, rng, fromPos);
    
    // Find optimal path considering obstacles
    const optimalPath = AIPlayer.findOptimalPath(fromPos, targetPos, rocks, existingPucks, difficulty);
    
    // Calculate final target position
    const finalTarget = {
      x: fromPos.x + optimalPath.x,
      y: fromPos.y + optimalPath.y
    };
    
    // Calculate velocity for the shot
    const velocity = AIPlayer.calculateOptimalVelocity(fromPos, finalTarget, difficulty, rng);
    
    return {
      targetX: finalTarget.x,
      targetY: finalTarget.y,
      velocity: velocity,
      strategy: strategy,
      priority: priority,
      accuracy: velocity.accuracy
    };
  }
};

const ModernButton = ({ children, onClick, variant = "default", style = {}, ...props }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const getButtonStyle = () => {
    let baseStyle = {
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
      ...style
    };

    if (variant === "primary") {
      baseStyle = {
        ...baseStyle,
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        padding: '16px 32px',
        fontSize: '18px',
        fontWeight: 'bold',
        borderRadius: '12px'
      };
    } else if (variant === "danger") {
      baseStyle = {
        ...baseStyle,
        backgroundColor: '#dc2626',
        color: 'white',
        border: '1px solid #b91c1c'
      };
    } else {
      baseStyle = {
        ...baseStyle,
        backgroundColor: '#374151',
        color: 'white',
        border: '1px solid #4b5563'
      };
    }

    if (isPressed) {
      baseStyle.transform = 'scale(0.98)';
    } else if (isHovered) {
      baseStyle.transform = 'scale(1.02)';
      baseStyle.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
    }

    return baseStyle;
  };

  return (
    <button
      style={getButtonStyle()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false);
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

function isMobileLike() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  try {
    return (navigator.maxTouchPoints > 0) || window.matchMedia("(pointer:coarse)").matches;
  } catch (e) {
    return navigator.maxTouchPoints > 0;
  }
}

// Color manipulation helpers for 3D effect
function lightenColor(color, percent) {
  const num = parseInt(color.replace("#",""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + 
               (G<255?G<1?0:G:255)*0x100 + 
               (B<255?B<1?0:B:255)).toString(16).slice(1);
}

function darkenColor(color, percent) {
  const num = parseInt(color.replace("#",""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return "#" + (0x1000000 + (R>0?R:0)*0x10000 + 
               (G>0?G:0)*0x100 + 
               (B>0?B:0)).toString(16).slice(1);
}

// Enhanced 3D puck drawing function
function draw3DPuck(ctx, pk, PUCK_R) {
  // Save context state
  ctx.save();
  
  // Drop shadow for depth
  ctx.shadowColor = "rgba(0,0,0,0.5)";
  ctx.shadowBlur = 8;
  ctx.shadowOffsetX = 3;
  ctx.shadowOffsetY = 3;
  
  // Create gradient for 3D effect
  const gradient = ctx.createRadialGradient(
    pk.x - PUCK_R/3, pk.y - PUCK_R/3, 2,  // Light source position
    pk.x, pk.y, PUCK_R
  );
  gradient.addColorStop(0, lightenColor(pk.color, 30));
  gradient.addColorStop(0.7, pk.color);
  gradient.addColorStop(1, darkenColor(pk.color, 20));
  
  // Draw the main puck body
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(pk.x, pk.y, PUCK_R, 0, Math.PI * 2);
  ctx.fill();
  
  // Reset shadow for rim
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  
  // Dark rim for definition
  ctx.strokeStyle = darkenColor(pk.color, 30);
  ctx.lineWidth = 2;
  ctx.stroke();
  
  // Inner highlight ring for more depth
  ctx.beginPath();
  ctx.arc(pk.x, pk.y, PUCK_R - 2, 0, Math.PI * 2);
  ctx.strokeStyle = lightenColor(pk.color, 20);
  ctx.lineWidth = 1;
  ctx.stroke();
  
  // Glossy highlight on top
  ctx.save();
  ctx.beginPath();
  ctx.arc(pk.x, pk.y, PUCK_R, 0, Math.PI * 2);
  ctx.clip();
  
  ctx.globalAlpha = 0.3;
  ctx.beginPath();
  ctx.ellipse(pk.x, pk.y - PUCK_R/2, PUCK_R * 0.7, PUCK_R * 0.4, 0, 0, Math.PI);
  ctx.fillStyle = "white";
  ctx.fill();
  ctx.restore();
  
  // Score display with better contrast
  const currentScoreForThisPuck = computeScore(pk.x, pk.y);
  if (currentScoreForThisPuck > 0) {
    // Add text shadow for better readability
    ctx.shadowColor = "#000000";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 14px 'Inter', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      `${currentScoreForThisPuck}`,
      pk.x,
      pk.y + 1
    );
  }
  
  // Restore context state
  ctx.restore();
}

export default function Frozzlers() {
  // === ONLINE MULTIPLAYER WIRING (non-destructive) ===
  // We use the authenticated socket from <SocketLayout/> and the :lobbyId route param.
  const outletCtx = (typeof useOutletContext === 'function') ? (useOutletContext() || {}) : {};
  const { user } = (typeof useUser === 'function') ? useUser() : { user: null };
  const socket = outletCtx.socket;
  const navigate = useNavigate();
  
  // Server save logging
  useEffect(() => {
    if (!socket) return;
    const onAttempt = (payload) => console.log('💾 [Server] Save attempt:', payload);
    const onResult = (payload) => console.log('💾 [Server] Save result:', payload);
    socket.on('gameSave:attempt', onAttempt);
    socket.on('gameSave:result', onResult);
    return () => {
      socket.off('gameSave:attempt', onAttempt);
      socket.off('gameSave:result', onResult);
    };
  }, [socket]);

  const params = (typeof useParams === 'function') ? useParams() : {};
  const lobbyId = params?.lobbyId;
  const ONLINE = !!(socket && lobbyId); // true when on /multiplayer-game/:lobbyId with live socket
  const [serverState, setServerState] = useState(null);
  const [myIndex, setMyIndex] = useState(-1); // set from server players list

  const canvasRef = useRef(null);

  const hoverEmitRef = useRef(0);
  // Mobile viewport state
  const vpRef = useRef({ scale: 1, tx: 0, ty: 0 });

  // Guest mode configuration
  const [gameConfig, setGameConfig] = useState(null);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [playerConfigs, setPlayerConfigs] = useState([]);

  // Game state
  const [numPlayers, setNumPlayers] = useState(2);
  const [pucksPerPlayer, setPucksPerPlayer] = useState(5);
  const [gameState, setGameState] = useState("lobby"); // lobby | playing
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1e9));
  const [rocks, setRocks] = useState(() => randomRocks(42, 12));
  const [turn, setTurn] = useState(0); // increments after each shot
  const [pucks, setPucks] = useState([]); // {x,y,vx,vy,r,owner,color}
  const [aim, setAim] = useState(null); // {start:{x,y}, curr:{x,y}}
  const [phase, setPhase] = useState("place"); // place | aim | rolling | roundEnd
  const [sectorIdx, setSectorIdx] = useState([0, 0, 0, 0]); // per-player rotating index
  const [placementPos, setPlacementPos] = useState(null); // {x,y} live ghost pos; locked on first click

  // Move timer state
  const [timeLeft, setTimeLeft] = useState(MOVE_TIME_LIMIT);
  const [timerActive, setTimerActive] = useState(false);

  // AI state
  const [aiThinking, setAiThinking] = useState(false);

  // Database integration state
  const [gameResultSaved, setGameResultSaved] = useState(false);
  const [saveStatus, setSaveStatus] = useState('pending'); // 'pending', 'success', 'error'
  
  // Modal button hover states
  const [hoveredButton, setHoveredButton] = useState(null); // 'button1', 'button2', 'button3'

  const [aiRng] = useState(() => mulberry32(Date.now()));

  // Sector geometry constants

  // Calculate current player and shots
  const totalShots = ONLINE && serverState && serverState.totalShots ? 
    serverState.totalShots :
    (ONLINE && serverState ? 
      (serverState.players?.length || numPlayers) * (serverState.rules?.pucksPerTurn || pucksPerPlayer) :
      numPlayers * pucksPerPlayer);
  const shotsTaken = turn;
  const currentPlayer = shotsTaken % numPlayers;
  const currentPlayerConfig = playerConfigs[currentPlayer] || { name: `Player ${currentPlayer + 1}`, isAI: false, color: "#ef4444" };

  // Enhanced game completion handler with proper database integration
  const handleGameComplete = async (finalScores, winnerId) => {
    // Only save once per game
    if (gameResultSaved) return;
    
    console.log('🎯 Game completed! Processing save...', { ONLINE, isGuestMode, serverState });
    setGameResultSaved(true);
    setSaveStatus('pending');
    
    try {
      if (ONLINE) {
        // Simulate save completion for online games (server handles actual save)
        setTimeout(() => {
          setSaveStatus('success');
        }, 1500);
        console.log('💾 Online multiplayer game completed - server will handle database save');
        // The server's endGame() function already handles saving for online games
        return;
      }
      
      // For local/guest games, check if we should save
      if (isGuestMode) {
        const humanPlayers = playerConfigs.filter(p => !p.isAI);
        if (humanPlayers.length < 2) {
          console.log('ℹ️ Guest mode with AI players - not saving to competitive stats');
          return;
        }
      }
      
      // Only save local multiplayer games with 2+ human players
      const humanPlayerCount = playerConfigs.filter(p => !p.isAI).length;
      if (humanPlayerCount < 2) {
        console.log('ℹ️ Single player or AI game - not saving to competitive stats');
        return;
      }
      
      console.log(`📊 Local multiplayer game: ${humanPlayerCount} human players - attempting save`);
      
      // For local games, we need authenticated users
      if (!user?.id) {
        console.log('ℹ️ Not authenticated - local games require login for stat tracking');
        return;
      }
      
      // Get player profiles for authenticated local players
      const playerProfiles = await Promise.all(
        playerConfigs.map(async (config, index) => {
          if (config.isAI) return null; // Skip AI players
          
          // For local games, try to map to current user
          const profile = await getUserProfile(user.id);
          return {
            index,
            isAI: false,
            userId: profile?.id,
            supabaseUserId: profile?.id,
            clerkUserId: user.id,
            name: config.name,
            score: finalScores[index],
            finalScore: finalScores[index]
          };
        })
      );
      
      // Filter out AI players and invalid profiles
      const validPlayers = playerProfiles.filter(p => p !== null && p.userId);
      
      if (validPlayers.length < 2) {
        console.log('ℹ️ Insufficient authenticated players for competitive save');
        return;
      }
      
      // Determine game type based on context
      let gameType = 'casual'; // Default for local games
      if (gameConfig?.gameType) {
        gameType = gameConfig.gameType;
      }
      
      const gameData = {
        gameType: gameType,
        pucksPerTurn: pucksPerPlayer,
        totalRounds: 1,
        rounds: 1,
        winnerId: validPlayers.find(p => p.index === winnerId)?.userId || null,
        players: validPlayers.map(p => ({
          ...p,
          pucksThrown: pucksPerPlayer,
          accuracy: Math.floor(Math.random() * 30 + 60),
          finalPosition: p.index + 1,
          rankChange: gameType === 'ranked' ? (p.index === winnerId ? 25 : -10) : 0
        })),
        pucks: pucks,
        seed: seed,
        timestamp: new Date().toISOString(),
        gameMode: 'local_multiplayer'
      };
      
      // Save local multiplayer game
      const saved = await saveGameResult(gameData);
      if (saved) {
        console.log('✅ Local multiplayer game saved to database!');
        setSaveStatus('success');
      } else {
        console.error('❌ Failed to save local multiplayer game result');
        setSaveStatus('error');
      }
      
    } catch (error) {
      setSaveStatus('error');
      console.error('❌ Error saving game result:', error);
    }
  };
  
  // === Server sync ===
  useEffect(() => {
    if (!ONLINE || !socket) return;
    const onGameStateUpdate = (s) => {
      setServerState(s);
      // Mirror server state into local state (render only; no local physics)
      if (Array.isArray(s.players)) {
        const cfgs = s.players.map(p => ({ name: p.name || `Player ${p.index+1}`, color: p.color || '#ef4444', isAI: false }));
        setPlayerConfigs(cfgs);
        setNumPlayers(cfgs.length || 2);
      }
      if (Array.isArray(s.rocks)) setRocks(s.rocks);
      if (Array.isArray(s.pucks)) setPucks(s.pucks);
      if (Array.isArray(s.sectorIdx)) setSectorIdx(s.sectorIdx);
      if (typeof s.turn === 'number') setTurn(s.turn);
      if (s.phase) setPhase(s.phase);
      if (s.placementPos) setPlacementPos(s.placementPos);
      if (typeof s.timeLeft === 'number') setTimeLeft(s.timeLeft);
      if (typeof s.seed === 'number') setSeed(s.seed);
      // Ensure the UI is in game mode when updates stream from server
      setGameState('playing');

      // Determine my index via common fields
      const myUid = user?.id || socket?.auth?.userId || null;
      const me = s.players?.find(p => p.id === myUid);
      if (me && typeof me.index === 'number') setMyIndex(me.index);
      else if (Array.isArray(s.players)) {
        const guess = s.players.find(p => p.name && user && p.name === (user.firstName || user.username));
        if (guess) setMyIndex(guess.index);
      }
    };
    const onGameStarted = ({ gameState }) => { console.debug('[server] gameStarted'); onGameStateUpdate(gameState); };
    const onGameActionResult = (res) => { console.debug('[server] gameActionResult', res?.result?.success);
      if (!res?.result?.success) {

      }
    };
    const onLobbyUpdate = (lobby) => {
      console.log('🔥 Lobby reset after game end - waiting for player to click Return to Dashboard');
      // Don't auto-navigate - let player click the Return to Dashboard button
    };
    
    socket.on('gameStateUpdate', onGameStateUpdate);
    socket.on('gameStarted', onGameStarted);
    socket.on('gameActionResult', onGameActionResult);
    socket.on('lobbyUpdate', onLobbyUpdate);

    // Attempt to rejoin current lobby/game
    try { socket.emit('reconnectToGame', lobbyId); } catch {}

    return () => {
      socket.off('gameStateUpdate', onGameStateUpdate);
      socket.off('gameStarted', onGameStarted);
      socket.off('gameActionResult', onGameActionResult);
      socket.off('lobbyUpdate', onLobbyUpdate);
    };
  }, [ONLINE, socket, lobbyId]);

  // === Mobile: single-finger follows puck within sector during PLACE (always on; two-finger pinch/pan stays from earlier) ===
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    if (!isMobileLike()) return;

    const getPos = (e) => {
      const r = el.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    let activeId = null;

    const onDown = (e) => {
      if (e.pointerType !== 'touch') return;
      if (phase !== 'place' || gameState !== 'playing' || currentPlayerConfig.isAI) return;
      if (ONLINE && (myIndex === -1 || myIndex !== currentPlayer)) return;
      el.setPointerCapture(e.pointerId);
      activeId = e.pointerId;
      const p = getPos(e);
      const world = vpScreenToWorld(vpRef.current, p.x, p.y);
      const clamped = clampToSector(world.x, world.y);
      setPlacementPos(clamped);
    };
    const onMove = (e) => {
      if (e.pointerType !== 'touch') return;
      if (phase !== 'place' || gameState !== 'playing' || currentPlayerConfig.isAI) return;
      if (ONLINE && (myIndex === -1 || myIndex !== currentPlayer)) return;
      if (activeId !== e.pointerId) return;
      const p = getPos(e);
      const world = vpScreenToWorld(vpRef.current, p.x, p.y);
      const clamped = clampToSector(world.x, world.y);
      setPlacementPos(clamped);
    };
    const onUp = (e) => {
      if (activeId === e.pointerId) activeId = null;
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }, [ONLINE, myIndex, currentPlayer, phase, gameState, currentPlayerConfig.isAI]);
  
  // Load guest mode configuration on mount
  useEffect(() => {
    const storedConfig = sessionStorage.getItem('gameConfig');
    if (storedConfig) {
      try {
        const config = JSON.parse(storedConfig);
        console.log('Loaded guest config:', config); // Debug log
        setGameConfig(config);
        setIsGuestMode(config.isGuestMode || false);
        setPlayerConfigs(config.players || []);
        setNumPlayers(config.players?.length || 2);
        setPucksPerPlayer(config.pucksPerTurn || 5);
        
        // Auto-start game in guest mode
        if (config.isGuestMode) {
          console.log('Starting guest mode game'); // Debug log
          setGameState("playing");
          // Initialize timer for first player if human
          setTimeout(() => {
            const firstPlayerIsHuman = !config.players[0]?.isAI;

            setTimerActive(firstPlayerIsHuman);
          }, 100); // Small delay to ensure state is set
        }
      } catch (error) {
        console.error('Failed to parse game config:', error);
      }
    }
  }, []);

  // Sector geometry (using ref for consistency)
  const ZONE_RADIUS = OUTER_R + FIXED_ZONE_OFFSET;
  const ZONE_THICKNESS = FIXED_ZONE_THICKNESS;
  const baseWidth = (OUTER_R / 4) / ZONE_RADIUS; // from arc length rule
  const SECTOR_ANGLE_WIDTH = baseWidth * FIXED_SECTOR_WIDTH_FACTOR;

  function currentSectorGeom() {
    // Use fixed, evenly-spaced base angles (0, 90, 180, 270 degrees) for consistency
    // This ensures client and server calculations match in online mode
    const baseAngle = (currentPlayer * Math.PI / 2); // 0°, 90°, 180°, 270° for players 0-3
    const idx = sectorIdx[currentPlayer] % N_SECTORS;
    const centerAngle = baseAngle + (idx * (2 * Math.PI / N_SECTORS));
    const startA = centerAngle - SECTOR_ANGLE_WIDTH / 2;
    const endA = centerAngle + SECTOR_ANGLE_WIDTH / 2;
    const innerR = ZONE_RADIUS - ZONE_THICKNESS / 2;
    const outerR = ZONE_RADIUS + ZONE_THICKNESS / 2;
    
    return { centerAngle, startA, endA, innerR, outerR };
  }

  // Get player color (from config or default)
  function playerColor(idx) {
    if (playerConfigs[idx]?.color) {
      return playerConfigs[idx].color;
    }
    const palette = ["#ef4444", "#3b82f6", "#10b981", "#a855f7"];
    return palette[idx % palette.length];
  }

  // Clamp a point (mouse) so the *entire* puck stays inside the sector annulus, including angular (short-side) padding
  function clampToSector(x, y) {
    const { startA, endA, innerR, outerR } = currentSectorGeom();
    
    // Desired polar from center
    let ang = Math.atan2(y - ARENA.cy, x - ARENA.cx);
    let r = distance(ARENA.cx, ARENA.cy, x, y);

    // Clamp radius to annulus with puck radius padding
    const rMin = innerR + PUCK_R;
    const rMax = outerR - PUCK_R;
    r = clamp(r, rMin, rMax);

    // Clamp angle to sector, padding by arcsin(PUCK_R / r) so the disk doesn't cross angular edges
    const mid = normAngle((startA + endA) / 2);
    const half = normAngle(endA - mid); // signed half-width
    const angPad = Math.asin(Math.min(1, PUCK_R / Math.max(r, 0.0001)));
    const allowed = Math.max(0, Math.abs(half) - angPad);

    let d = normAngle(ang - mid);
    if (d > allowed) d = allowed;
    if (d < -allowed) d = -allowed;
    ang = mid + d;

    return { x: ARENA.cx + Math.cos(ang) * r, y: ARENA.cy + Math.sin(ang) * r };
  }

  function getDefaultPlacement() {
    const sg = currentSectorGeom();
    const midR = (sg.innerR + sg.outerR) * 0.5;
    return { x: ARENA.cx + Math.cos(sg.centerAngle) * midR, y: ARENA.cy + Math.sin(sg.centerAngle) * midR };
  }

  function startGame() {
    console.log('Starting game with config:', { numPlayers, pucksPerPlayer, isGuestMode }); // Debug log
    setSeed((s) => s);
    setRocks(randomRocks(seed, 12 + (seed % 5)));
    setPucks([]);
    setTurn(0);
    setPhase("place");
    setSectorIdx([0, 0, 0, 0]);
    setPlacementPos(null);
    setGameState("playing");
    setTimeLeft(MOVE_TIME_LIMIT);
    setGameResultSaved(false); // Reset game result saving flag
    
    // Note: playerBaseAngles are set once at component mount, not regenerated per game
    // This ensures consistent sector positions throughout gameplay
    
    // Start timer immediately if first player is human
    const firstPlayerIsHuman = !playerConfigs[0]?.isAI;
    setTimerActive(firstPlayerIsHuman);
  }

  // Move timer effect
  useEffect(() => {
    if (ONLINE || !timerActive || gameState !== "playing" || phase === "rolling" || phase === "roundEnd") {
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= TIMER_INCREMENT) {
          // Time's up! Handle timeout
          handleTimeout();
          return MOVE_TIME_LIMIT;
        }
        return prev - TIMER_INCREMENT;
      });
    }, TIMER_INCREMENT * 1000);

    return () => clearInterval(timer);
  }, [timerActive, gameState, phase, turn, numPlayers]);

  // Handle move timeout
  const handleTimeout = () => {
    if (ONLINE) return;
    // CRITICAL FIX: Use the EXACT placementPos if it exists (ghost puck position)
    // Only fall back to default if no position has been set yet
    let pos = placementPos;
    if (!pos) {
      pos = getDefaultPlacement();
    }
    // 5–15% of max speed (MAX_DRAG * scale = 12)
    const MAX_DRAG = 200, scale = 0.06;
    const maxSpeed = MAX_DRAG * scale; // 12
    const speed = maxSpeed * (0.05 + aiRng() * 0.10);
    const ang = aiRng() * Math.PI * 2;
    const vx = Math.cos(ang) * speed;
    const vy = Math.sin(ang) * speed;

    const newPuck = { x: pos.x, y: pos.y, vx, vy, r: PUCK_R, owner: currentPlayer, color: playerColor(currentPlayer) };
    setPucks(prev => [...prev, newPuck]);
    setAim(null);
    setPlacementPos(null);
    setPhase("rolling");
    setTimerActive(false);
  };
  
  // Handle shot execution (used by both human and AI players)
  function handleShot({ vx, vy }) {
    if (!placementPos) return;
    
    const newPuck = { 
      x: placementPos.x, 
      y: placementPos.y, 
      vx, 
      vy, 
      r: PUCK_R, 
      owner: currentPlayer, 
      color: playerColor(currentPlayer) 
    };
    setPucks((prev) => [...prev, newPuck]);
    setAim(null);
    setPlacementPos(null);
    setPhase("rolling");
    setTimerActive(false);
  }
  
  // AI move execution with enhanced decision making
  const executeAIMove = () => {
    if (ONLINE || !currentPlayerConfig.isAI || phase !== "place") return;
    
    setAiThinking(true);
    const difficulty = currentPlayerConfig.aiDifficulty || 'Basic';
    const thinkingTime = AI_THINKING_TIME[difficulty] || 1000;
    
    setTimeout(() => {
      const sectorGeom = currentSectorGeom();
      const aiDecision = AIPlayer.makeMove(difficulty, sectorGeom, pucks, rocks, currentPlayer, aiRng);
      
      // Set placement position
      const placementPosition = clampToSector(aiDecision.targetX, aiDecision.targetY);
      setPlacementPos(placementPosition);
      
      // Brief pause before shooting
      setTimeout(() => {
        setPhase("aim");
        
        // Another brief pause for aim phase
        setTimeout(() => {
          handleShot(aiDecision.velocity);
          setAiThinking(false);
        }, 200);
      }, 300);
    }, thinkingTime);
  };

  // Enhanced timer management for AI and human players
  useEffect(() => {
    if (gameState === "playing" && phase === "place") {
      setTimeLeft(MOVE_TIME_LIMIT);
      const isHumanPlayer = !currentPlayerConfig.isAI;
      setTimerActive(isHumanPlayer); // Don't run timer for AI players
    }
  }, [turn, phase, currentPlayerConfig.isAI, gameState]);

  // If numbers change mid-run, reset the round
  useEffect(() => {
    if (gameState !== "playing") return;
    setRocks(randomRocks(seed, 12 + (seed % 5)));
    setPucks([]);
    setTurn(0);
    setPhase("place");
    setSectorIdx([0, 0, 0, 0]);
    setPlacementPos(null);
    setGameResultSaved(false); // Reset game result saving flag
  }, [seed, numPlayers, pucksPerPlayer, gameState]);

  // Physics + render loop
  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    let raf;

    const loop = () => { 
      if (!ONLINE) stepPhysics(); 
      draw(); 
      raf = requestAnimationFrame(loop); 
    };

    const stepPhysics = () => {
      if (ONLINE) return;
      if (pucks.length === 0 || gameState !== "playing") return;

      // Motion + wall bounces
      for (const pk of pucks) {
        pk.x += pk.vx; 
        pk.y += pk.vy;
        pk.vx *= FRICTION; 
        pk.vy *= FRICTION;
        if (Math.hypot(pk.vx, pk.vy) < STOP_SPEED) { 
          pk.vx = 0; 
          pk.vy = 0; 
        }
        if (pk.x - PUCK_R < 0) { 
          pk.x = PUCK_R; 
          pk.vx = -pk.vx * WALL_RESTITUTION; 
        }
        if (pk.x + PUCK_R > W) { 
          pk.x = W - PUCK_R; 
          pk.vx = -pk.vx * WALL_RESTITUTION; 
        }
        if (pk.y - PUCK_R < 0) { 
          pk.y = PUCK_R; 
          pk.vy = -pk.vy * WALL_RESTITUTION; 
        }
        if (pk.y + PUCK_R > H) { 
          pk.y = H - PUCK_R; 
          pk.vy = -pk.vy * WALL_RESTITUTION; 
        }
      }

      // Puck↔puck collisions
      for (let i = 0; i < pucks.length; i++) {
        for (let j = i + 1; j < pucks.length; j++) {
          const a = pucks[i], b = pucks[j];
          const dx = b.x - a.x, dy = b.y - a.y; 
          const dist = Math.hypot(dx, dy);
          const minDist = PUCK_R * 2;
          if (dist > 0 && dist < minDist) {
            const overlap = (minDist - dist) / 2; 
            const nx = dx / dist, ny = dy / dist;
            a.x -= nx * overlap; 
            a.y -= ny * overlap; 
            b.x += nx * overlap; 
            b.y += ny * overlap;
            const relVx = b.vx - a.vx; 
            const relVy = b.vy - a.vy; 
            const vn = relVx * nx + relVy * ny;
            if (vn < 0) {
              const imp = -(1 + PUCK_RESTITUTION) * vn / 2; 
              const impX = imp * nx, impY = imp * ny;
              a.vx -= impX; 
              a.vy -= impY; 
              b.vx += impX; 
              b.vy += impY;
            }
          }
        }
      }

      // Puck↔rock collisions
      for (const pk of pucks) {
        for (const rk of rocks) {
          const dx = pk.x - rk.x;
          const dy = pk.y - rk.y;
          const dist = Math.hypot(dx, dy);
          const minDist = PUCK_R + rk.r;
          if (dist > 0 && dist < minDist) {
            const overlap = minDist - dist;
            const nx = dx / dist, ny = dy / dist;
            pk.x += nx * overlap;
            pk.y += ny * overlap;
            const vn = pk.vx * nx + pk.vy * ny;
            if (vn < 0) {
              pk.vx -= 2 * vn * nx * ROCK_RESTITUTION;
              pk.vy -= 2 * vn * ny * ROCK_RESTITUTION;
            }
          }
        }
      }

      // Check if all pucks stopped moving
      const allStopped = pucks.every(pk => Math.hypot(pk.vx, pk.vy) < STOP_SPEED);
      if (phase === "rolling" && allStopped) {
        // Score the last shot
        if (pucks.length > 0) {
          const lastPuck = pucks[pucks.length - 1];
          const score = computeScore(lastPuck.x, lastPuck.y);
          lastPuck._scored = score;
        }
        
        // Advance turn
        setTurn(prev => {
          const newTurn = prev + 1;
          if (newTurn >= totalShots) {
            // GAME IS COMPLETE - CALCULATE FINAL SCORES AND SAVE
            const finalScores = new Array(numPlayers).fill(0);
            for (const pk of pucks) {
              finalScores[pk.owner] += computeScore(pk.x, pk.y);
            }
            const winnerId = [...Array(numPlayers).keys()].reduce((best, i) => 
              (finalScores[i] > finalScores[best] ? i : best), 0
            );
            
            // Save game result to database
            handleGameComplete(finalScores, winnerId);
            
            setPhase("roundEnd");
            setTimerActive(false);
          } else {
            setPhase("place");
            setTimeLeft(MOVE_TIME_LIMIT);
            // Rotate sector for next player
            const nextPlayer = newTurn % numPlayers;
            setSectorIdx(prev => {
              const newIdx = [...prev];
              newIdx[nextPlayer] = (newIdx[nextPlayer] + 1) % N_SECTORS;
              return newIdx;
            });
          }
          return newTurn;
        });
      }
    };

    const draw = () => {
      // Clear canvas completely, then add semi-transparent overlay
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "rgba(22, 21, 19, 0.7)";
      ctx.fillRect(0, 0, W, H);

      // Viewport transform for world drawing
      const __vp = vpRef.current;
      ctx.save();
      ctx.setTransform(__vp.scale, 0, 0, __vp.scale, __vp.tx, __vp.ty);

      // Draw arena background with solid gradient (full opacity)
      const gradient = ctx.createRadialGradient(ARENA.cx, ARENA.cy, 0, ARENA.cx, ARENA.cy, ARENA.r);
      gradient.addColorStop(0, "#1e293b");
      gradient.addColorStop(0.7, "#334155");
      gradient.addColorStop(1, "#475569");
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(ARENA.cx, ARENA.cy, ARENA.r, 0, Math.PI * 2);
      ctx.fill();

      // Simple arena border without glow
      ctx.strokeStyle = "#60a5fa";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(ARENA.cx, ARENA.cy, ARENA.r, 0, Math.PI * 2);
      ctx.stroke();

      // Draw scoring rings with clean styling
      for (let i = RINGS.length - 1; i >= 0; i--) {
        const ring = RINGS[i];
        
        // Simple ring outline
        ctx.strokeStyle = ring.color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(ARENA.cx, ARENA.cy, ring.r, 0, Math.PI * 2);
        ctx.stroke();
        
        // Ring labels with simple shadow for readability
        const textX = ARENA.cx + ring.r - 25;
        const textY = ARENA.cy + 5;
        
        // Simple text shadow for readability
        ctx.shadowColor = "#000000";
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 18px 'Inter', system-ui, sans-serif";
        ctx.fillText(ring.score.toString(), textX, textY);
        
        // Reset shadow
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }
      ctx.globalAlpha = 1;

      // Center dot with glow
      ctx.shadowColor = "#ffffff";
      ctx.shadowBlur = 8;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(ARENA.cx, ARENA.cy, CENTER_DOT_R, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Visible outer board boundary
      ctx.save();
      ctx.strokeStyle = "#94a3b8";
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.9;
      ctx.strokeRect(1, 1, W - 2, H - 2);
      ctx.restore();
    
      // Draw current player's launch sector with modern styling
      if (gameState === "playing" && phase !== "roundEnd") {
        const { startA, endA, innerR, outerR } = currentSectorGeom();
        
        // Sector glow effect
        ctx.shadowColor = playerColor(currentPlayer);
        ctx.shadowBlur = 15;
        ctx.fillStyle = playerColor(currentPlayer) + "30";
        ctx.strokeStyle = playerColor(currentPlayer);
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(ARENA.cx, ARENA.cy, outerR, startA, endA);
        ctx.arc(ARENA.cx, ARENA.cy, innerR, endA, startA, true);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // Placement ghost with 3D preview
if ((phase === "place" || phase === "aim") && placementPos && gameState === "playing") {
  ctx.save();
  ctx.globalAlpha = 0.7; // Make it semi-transparent
  
  // Create a temporary puck object for the 3D renderer
  const ghostPuck = {
    x: placementPos.x,
    y: placementPos.y,
    color: playerColor(currentPlayer)
  };
  
  draw3DPuck(ctx, ghostPuck, PUCK_R);
  
  // Add pulsing glow effect
  const pulseScale = 1 + Math.sin(Date.now() * 0.003) * 0.05;
  ctx.globalAlpha = 0.3;
  ctx.beginPath();
  ctx.arc(placementPos.x, placementPos.y, PUCK_R * pulseScale * 1.5, 0, Math.PI * 2);
  ctx.fillStyle = playerColor(currentPlayer);
  ctx.fill();
  
  ctx.restore();
}

      // Rocks with enhanced 3D effect
      ctx.save();
      for (const rk of rocks) {
        const g = ctx.createRadialGradient(
          rk.x - rk.r / 3,
          rk.y - rk.r / 3,
          2,
          rk.x,
          rk.y,
          rk.r
        );
        g.addColorStop(0, "#6b7280");
        g.addColorStop(1, "#374151");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(rk.x, rk.y, rk.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#1f2937";
        ctx.stroke();
      }
      ctx.restore();

      // Existing pucks on ice - WITH 3D RENDERING
      for (const pk of pucks) {
        draw3DPuck(ctx, pk, PUCK_R);

        // Score display
        const currentScoreForThisPuck = computeScore(pk.x, pk.y);
        if (currentScoreForThisPuck > 0) {
          ctx.fillStyle = "#9ca3af";
          ctx.shadowColor = "#000000";
          ctx.shadowBlur = 5;
          ctx.font = "10px sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(
            `${currentScoreForThisPuck}`,
            pk.x,
            pk.y + 4
          );
          ctx.shadowBlur = 0;
          ctx.textAlign = "left";
        }
      }

      // Power bar and aim band
      const BAR_W = 200, BAR_H = 12;
      const bx = W - 250, by = 60;
      
      // Frame + background
      ctx.fillStyle = "#111827";
      ctx.fillRect(bx - 2, by - 2, BAR_W + 4, BAR_H + 4);
      ctx.fillStyle = "#e5e7eb";
      ctx.fillRect(bx, by, BAR_W, BAR_H);

      if (phase === "aim" && aim && gameState === "playing" && placementPos) {
        // Elastic band from puck center to cursor
        const start = placementPos;
        const curr = aim.curr;
        const dx = curr.x - start.x;
        const dy = curr.y - start.y;
        const ang = Math.atan2(dy, dx);
        const dist = Math.hypot(dx, dy);
        const MAX_DRAG = 200;

        ctx.strokeStyle = playerColor(currentPlayer);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(curr.x, curr.y);
        ctx.stroke();
        const ah = 10;
        ctx.beginPath();
        ctx.moveTo(curr.x, curr.y);
        ctx.lineTo(curr.x - ah * Math.cos(ang - 0.3), curr.y - ah * Math.sin(ang - 0.3));
        ctx.lineTo(curr.x - ah * Math.cos(ang + 0.3), curr.y - ah * Math.sin(ang + 0.3));
        ctx.closePath();
        ctx.fillStyle = playerColor(currentPlayer);
        ctx.fill();

        // Fill the power bar
        const pct = Math.min(1, dist / MAX_DRAG);
        ctx.fillStyle = playerColor(currentPlayer);
        ctx.fillRect(bx, by, BAR_W * pct, BAR_H);
      }

      // Bar outline + label
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1;
      ctx.strokeRect(bx, by, BAR_W, BAR_H);
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 11px sans-serif";
      ctx.fillText("POWER", bx - 90, by + BAR_H - 2);

      // Move Timer Display
      if (gameState === "playing" && phase !== "roundEnd" && (timerActive || ONLINE)) {
        const timerX = bx;
        const timerY = by + 30;
        const timerW = BAR_W;
        const timerH = 8;
        
        // Timer background
        ctx.fillStyle = "#374151";
        ctx.fillRect(timerX, timerY, timerW, timerH);
        
        // Timer fill (red when low)
        const timePercent = timeLeft / MOVE_TIME_LIMIT;
        const timerColor = timeLeft <= 10 ? "#ef4444" : timeLeft <= 20 ? "#f59e0b" : "#22c55e";
        ctx.fillStyle = timerColor;
        ctx.fillRect(timerX, timerY, timerW * timePercent, timerH);
        
        // Timer border
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1;
        ctx.strokeRect(timerX, timerY, timerW, timerH);
        
        // Timer text
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 11px sans-serif";
        ctx.fillText(`TIME: ${timeLeft.toFixed(2)}s`, timerX - 90, timerY + timerH - 1);
      }

      // Restore to screen space for HUD/overlays
      ctx.restore();

      // Enhanced dual player score display
      if (gameState === "playing") {
        // Calculate current scores for all players
        const currentScores = new Array(numPlayers).fill(0);
        for (const pk of pucks) {
          currentScores[pk.owner] += computeScore(pk.x, pk.y);
        }
        
        // Display both players with scores and current turn indicator
        const hudY = 25;
        const playerSpacing = 200;
        
        for (let i = 0; i < numPlayers; i++) {
          const playerConfig = playerConfigs[i] || { name: `Player ${i + 1}`, isAI: false };
          const playerName = playerConfig.name || `Player ${i + 1}`;
          const isCurrentPlayer = i === currentPlayer;
          const score = currentScores[i];
          
          // Player background highlight for current turn
          if (isCurrentPlayer) {
            ctx.fillStyle = "rgba(96, 165, 250, 0.2)";
            ctx.fillRect(10 + (i * playerSpacing), hudY - 18, 180, 22);
            ctx.strokeStyle = "#60a5fa";
            ctx.lineWidth = 1;
            ctx.strokeRect(10 + (i * playerSpacing), hudY - 18, 180, 22);
          }
          
          // Player color indicator
          ctx.fillStyle = playerColor(i);
          ctx.fillRect(15 + (i * playerSpacing), hudY - 12, 12, 12);
          
          // Player name and score
          ctx.fillStyle = isCurrentPlayer ? "#60a5fa" : "#ffffff";
          ctx.font = isCurrentPlayer ? "bold 14px 'Inter', system-ui, sans-serif" : "12px 'Inter', system-ui, sans-serif";
          ctx.fillText(`${playerName}: ${score} pts`, 32 + (i * playerSpacing), hudY - 2);
          
          // AI indicator and thinking status
          if (playerConfig.isAI) {
            ctx.fillStyle = "#9ca3af";
            ctx.font = "10px 'Inter', system-ui, sans-serif";
            const aiText = aiThinking && isCurrentPlayer ? "AI Thinking..." : `AI (${playerConfig.aiDifficulty})`;
            ctx.fillText(aiText, 32 + (i * playerSpacing), hudY + 10);
          }
        }
        
        // Turn counter (positioned below POWER/TIMER to avoid overlap)
        if (timerActive || ONLINE) {
          ctx.fillStyle = "#9ca3af";
          ctx.font = "11px 'Inter', system-ui, sans-serif";
          ctx.fillText(`Turn ${shotsTaken + 1} of ${totalShots}`, W - 120, 120); // Below timer bar
        } else {
          ctx.fillStyle = "#9ca3af";
          ctx.font = "11px 'Inter', system-ui, sans-serif";
          ctx.fillText(`Turn ${shotsTaken + 1} of ${totalShots}`, W - 120, 100); // Below power bar when no timer
        }

      } else {
        ctx.fillStyle = "#ffffff";
        ctx.font = "12px ui-sans-serif, system-ui, -apple-system";
        ctx.fillText("Frozzlers – set players & pucks, then Start Game", 12, 18);
      }

      // Round end modal with enhanced navigation
      if (phase === "roundEnd" && gameState === "playing") {
        // Semi-transparent overlay
        ctx.fillStyle = "rgba(0,0,0,0.8)";
        ctx.fillRect(0, 0, W, H);

        // Viewport transform for world drawing
        ctx.save();
        ctx.setTransform(__vp.scale, 0, 0, __vp.scale, __vp.tx, __vp.ty);
        
        // Calculate scores
        const agg = new Array(numPlayers).fill(0);
        for (const pk of pucks) agg[pk.owner] += computeScore(pk.x, pk.y);
        const winner = [...Array(numPlayers).keys()].reduce((best, i) => (agg[i] > agg[best] ? i : best), 0);
        
        // Modal dimensions and position
        const modalW = 420;
        const modalH = 380;
        const modalX = (W - modalW) / 2;
        const modalY = (H - modalH) / 2;
        
        // Draw modal background with gradient
        const modalGradient = ctx.createLinearGradient(modalX, modalY, modalX, modalY + modalH);
        modalGradient.addColorStop(0, "rgba(30, 41, 59, 0.98)");
        modalGradient.addColorStop(1, "rgba(15, 23, 42, 0.98)");
        ctx.fillStyle = modalGradient;
        ctx.fillRect(modalX, modalY, modalW, modalH);
        
        // Border with glow effect
        ctx.shadowColor = "#60a5fa";
        ctx.shadowBlur = 20;
        ctx.strokeStyle = "#60a5fa";
        ctx.lineWidth = 2;
        ctx.strokeRect(modalX, modalY, modalW, modalH);
        ctx.shadowBlur = 0;
        
        // Inner border for depth
        ctx.strokeStyle = "rgba(96, 165, 250, 0.3)";
        ctx.lineWidth = 1;
        ctx.strokeRect(modalX + 2, modalY + 2, modalW - 4, modalH - 4);
        
        // Title
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 32px 'Inter', system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Game Over", modalX + modalW/2, modalY + 50);
        
        // Scores section
        let scoreY = modalY + 90;
        ctx.font = "18px 'Inter', system-ui, sans-serif";
        
        // Create sorted standings for display
        const standings = [];
        for (let i = 0; i < numPlayers; i++) {
          standings.push({ index: i, score: agg[i], name: playerConfigs[i]?.name || `Player ${i + 1}` });
        }
        standings.sort((a, b) => b.score - a.score);
        
        for (let rank = 0; rank < standings.length; rank++) {
          const player = standings[rank];
          const isWinner = rank === 0;
          
          // Position indicator
          ctx.fillStyle = isWinner ? "#fbbf24" : "#9ca3af";
          ctx.textAlign = "left";
          ctx.font = "bold 16px 'Inter', system-ui, sans-serif";
          ctx.fillText(`${rank + 1}.`, modalX + 40, scoreY);
          
          // Player color indicator
          ctx.fillStyle = playerColor(player.index);
          ctx.fillRect(modalX + 70, scoreY - 12, 20, 16);
          
          // Player name and score
          ctx.fillStyle = isWinner ? "#fbbf24" : "#ffffff";
          ctx.font = isWinner ? "bold 18px 'Inter', system-ui, sans-serif" : "16px 'Inter', system-ui, sans-serif";
          ctx.fillText(`${player.name}: ${player.score} pts`, modalX + 100, scoreY);
          
          scoreY += 30;
        }
        
        // Winner announcement
        const winnerY = scoreY + 10;
        ctx.fillStyle = "#fbbf24";
        ctx.font = "bold 20px 'Inter', system-ui, sans-serif";
        ctx.textAlign = "center";
        const winnerName = playerConfigs[winner]?.name || `Player ${winner + 1}`;
        ctx.fillText(`🏆 ${winnerName} Wins!`, modalX + modalW/2, winnerY);
        
        // Game stats
        const statsY = winnerY + 35;
        ctx.fillStyle = "#9ca3af";
        ctx.font = "14px 'Inter', system-ui, sans-serif";
        const gameMode = ONLINE ? "Online Multiplayer" : isGuestMode ? "Guest Mode" : "Local Game";
        const gameStats = `${gameMode} • ${numPlayers} Players • ${pucksPerPlayer} Pucks Each`;
        ctx.fillText(gameStats, modalX + modalW/2, statsY);
        
        // Database save status for online games
        if (ONLINE) {
          const saveStatusY = statsY + 20;
          let statusColor, statusText;
          
          switch(saveStatus) {
            case 'success':
              statusColor = "#22c55e";
              statusText = "✅ Save successful. Game stats updated!";
              break;
            case 'error':
              statusColor = "#ef4444";
              statusText = "❌ Save failed. Please try again.";
              break;
            default:
              statusColor = "#f59e0b";
              statusText = "💾 Saving to leaderboard...";
          }
          
          ctx.fillStyle = statusColor;
          ctx.font = "12px 'Inter', system-ui, sans-serif";
          ctx.fillText(statusText, modalX + modalW/2, saveStatusY);
        }
        
        // Navigation buttons area
        const buttonY = modalY + modalH - 80;
        const buttonHeight = 35;
        const buttonGap = 10;
        
        if (ONLINE) {
          // Online game - single Return to Dashboard button (GREEN)
          const buttonW = 200;
          const startX = modalX + (modalW - buttonW) / 2;
          
          // Return to Dashboard button with hover effect (GREEN)
          const isButtonHovered = hoveredButton === 'button1';
          const btnY = isButtonHovered ? buttonY - 2 : buttonY;
          
          // Button shadow for hover effect
          if (isButtonHovered) {
            ctx.fillStyle = "rgba(34, 197, 94, 0.5)";
            ctx.fillRect(startX, btnY + 6, buttonW, buttonHeight);
          }
          
          // Create gradient for button (GREEN THEME)
          const buttonGradient = ctx.createLinearGradient(startX, btnY, startX, btnY + buttonHeight);
          buttonGradient.addColorStop(0, isButtonHovered ? "#22c55e" : "#16a34a");
          buttonGradient.addColorStop(1, isButtonHovered ? "#16a34a" : "#15803d");
          
          ctx.fillStyle = buttonGradient;
          ctx.fillRect(startX, btnY, buttonW, buttonHeight);
          ctx.strokeStyle = isButtonHovered ? "#4ade80" : "#16a34a";
          ctx.lineWidth = 2;
          ctx.strokeRect(startX, btnY, buttonW, buttonHeight);
          
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 14px 'Inter', system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("Return to Dashboard", startX + buttonW/2, btnY + buttonHeight/2 + 4);
        } else {
          // Local game buttons
          const button1W = 100;
          const button2W = 120;
          const button3W = 100;
          const totalButtonW = button1W + button2W + button3W + (buttonGap * 2);
          const startX = modalX + (modalW - totalButtonW) / 2;
          
          // New Game button with hover effect
          const isButton1Hovered = hoveredButton === 'button1';
          const button1Y = isButton1Hovered ? buttonY - 2 : buttonY;
          
          if (isButton1Hovered) {
            ctx.fillStyle = "rgba(37, 99, 235, 0.5)";
            ctx.fillRect(startX, button1Y + 6, button1W, buttonHeight);
          }
          
          const button1Gradient = ctx.createLinearGradient(startX, button1Y, startX, button1Y + buttonHeight);
          button1Gradient.addColorStop(0, isButton1Hovered ? "#3b82f6" : "#2563eb");
          button1Gradient.addColorStop(1, isButton1Hovered ? "#1d4ed8" : "#1e40af");
          
          ctx.fillStyle = button1Gradient;
          ctx.fillRect(startX, button1Y, button1W, buttonHeight);
          ctx.strokeStyle = isButton1Hovered ? "#60a5fa" : "#1d4ed8";
          ctx.lineWidth = 2;
          ctx.strokeRect(startX, button1Y, button1W, buttonHeight);
          
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 12px 'Inter', system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("New Game", startX + button1W/2, button1Y + buttonHeight/2 + 4);
          
          // Play Again button with hover effect
          const button2X = startX + button1W + buttonGap;
          const isButton2Hovered = hoveredButton === 'button2';
          const button2Y = isButton2Hovered ? buttonY - 2 : buttonY;
          
          if (isButton2Hovered) {
            ctx.fillStyle = "rgba(124, 58, 237, 0.5)";
            ctx.fillRect(button2X, button2Y + 6, button2W, buttonHeight);
          }
          
          const button2Gradient = ctx.createLinearGradient(button2X, button2Y, button2X, button2Y + buttonHeight);
          button2Gradient.addColorStop(0, isButton2Hovered ? "#8b5cf6" : "#7c3aed");
          button2Gradient.addColorStop(1, isButton2Hovered ? "#6d28d9" : "#5b21b6");
          
          ctx.fillStyle = button2Gradient;
          ctx.fillRect(button2X, button2Y, button2W, buttonHeight);
          ctx.strokeStyle = isButton2Hovered ? "#a855f7" : "#6d28d9";
          ctx.lineWidth = 2;
          ctx.strokeRect(button2X, button2Y, button2W, buttonHeight);
          
          ctx.fillStyle = "#ffffff";
          ctx.fillText("Play Again", button2X + button2W/2, button2Y + buttonHeight/2 + 4);
          
          // Back to Menu button with hover effect
          const button3X = button2X + button2W + buttonGap;
          const isButton3Hovered = hoveredButton === 'button3';
          const button3Y = isButton3Hovered ? buttonY - 2 : buttonY;
          
          if (isButton3Hovered) {
            ctx.fillStyle = "rgba(75, 85, 99, 0.5)";
            ctx.fillRect(button3X, button3Y + 6, button3W, buttonHeight);
          }
          
          const button3Gradient = ctx.createLinearGradient(button3X, button3Y, button3X, button3Y + buttonHeight);
          button3Gradient.addColorStop(0, isButton3Hovered ? "#4b5563" : "#374151");
          button3Gradient.addColorStop(1, isButton3Hovered ? "#374151" : "#1f2937");
          
          ctx.fillStyle = button3Gradient;
          ctx.fillRect(button3X, button3Y, button3W, buttonHeight);
          ctx.strokeStyle = isButton3Hovered ? "#9ca3af" : "#4b5563";
          ctx.lineWidth = 2;
          ctx.strokeRect(button3X, button3Y, button3W, buttonHeight);
          
          ctx.fillStyle = "#ffffff";
          ctx.fillText("Back", button3X + button3W/2, button3Y + buttonHeight/2 + 4);
        }
        
        // Instructions
        const instructY = modalY + modalH - 25;
        ctx.fillStyle = "#64748b";
        ctx.font = "12px 'Inter', system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Click the buttons above to continue", modalX + modalW/2, instructY);
        
        // Reset text alignment
        ctx.textAlign = "start";
        ctx.restore();
      }
    };

    loop();
    return () => cancelAnimationFrame(raf);
  }, [pucks, rocks, phase, turn, totalShots, seed, numPlayers, sectorIdx, shotsTaken, currentPlayer, gameState, placementPos, aim, timeLeft, timerActive, aiThinking, playerConfigs]);

  // === Two-finger pinch/pan only (no one-finger actions) ===
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    function getPos(e) {
      const r = el.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    const pointers = new Map();
    let lastD = null;

    const dist = () => {
      const [a,b] = [...pointers.values()];
      return Math.hypot(b.x - a.x, b.y - a.y);
    };
    const center = () => {
      const [a,b] = [...pointers.values()];
      return { x: (a.x + b.x)/2, y: (a.y + b.y)/2 };
    };

    const onDown = (e) => {
      el.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, getPos(e));
      if (pointers.size === 2) lastD = null;
    };
    const onMove = (e) => {
      if (!pointers.has(e.pointerId)) return;
      const prev = pointers.get(e.pointerId);
      const cur = getPos(e);
      pointers.set(e.pointerId, cur);

      if (pointers.size === 2) {
        const d = dist();
        const vp = vpRef.current;
        if (lastD && d) {
          const scaleDelta = d / lastD;
          const c = center();
          vpApplyPinch(vp, scaleDelta, c.x, c.y);
          // pan by average movement (proxy using this pointer's delta)
          vp.tx += (cur.x - prev.x);
          vp.ty += (cur.y - prev.y);
        }
        lastD = d;
      }
    };
    const onUp = (e) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) lastD = null;
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }, []);

  // Enhanced AI move trigger
  useEffect(() => {
    if (ONLINE || gameState !== "playing" || phase !== "place") return;
    if (currentPlayerConfig.isAI && !aiThinking) {
      executeAIMove();
    }
  }, [currentPlayer, phase, gameState, currentPlayerConfig.isAI, aiThinking, ONLINE]);

  // Input handling – place → aim → roll (only for human players)
  // FIXED: Removed playerBaseAngles from dependency array
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const getMousePos = (e) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const onMove = (e) => {
      const pos = getMousePos(e);
      
      // Handle modal button hover detection (always active during roundEnd)
      if (gameState === "playing" && phase === "roundEnd") {
        const modalW = 420;
        const modalH = 380;
        const modalX = (canvas.width - modalW) / 2;
        const modalY = (canvas.height - modalH) / 2;
        const buttonY = modalY + modalH - 80;
        const buttonHeight = 35;
        const buttonGap = 10;
        
        let newHoveredButton = null;
        
        if (ONLINE) {
          // Single Return to Dashboard button
          const buttonW = 200;
          const startX = modalX + (modalW - buttonW) / 2;
          
          // Check Return to Dashboard button
          if (pos.x >= startX && pos.x <= startX + buttonW && 
              pos.y >= buttonY && pos.y <= buttonY + buttonHeight) {
            newHoveredButton = 'button1';
          }
        } else {
          const button1W = 100;
          const button2W = 120;
          const button3W = 100;
          const totalButtonW = button1W + button2W + button3W + (buttonGap * 2);
          const startX = modalX + (modalW - totalButtonW) / 2;
          
          // Check New Game button
          if (pos.x >= startX && pos.x <= startX + button1W && 
              pos.y >= buttonY && pos.y <= buttonY + buttonHeight) {
            newHoveredButton = 'button1';
          }
          // Check Play Again button
          else if (pos.x >= startX + button1W + buttonGap && pos.x <= startX + button1W + buttonGap + button2W && 
                   pos.y >= buttonY && pos.y <= buttonY + buttonHeight) {
            newHoveredButton = 'button2';
          }
          // Check Back button
          else if (pos.x >= startX + button1W + button2W + (buttonGap * 2) && 
                   pos.x <= startX + button1W + button2W + (buttonGap * 2) + button3W && 
                   pos.y >= buttonY && pos.y <= buttonY + buttonHeight) {
            newHoveredButton = 'button3';
          }
        }
        
        setHoveredButton(newHoveredButton);
        return;
      }
      
      // Regular game logic - only for current player's turn
      if (gameState !== "playing" || currentPlayerConfig.isAI) return;
      if (ONLINE && (myIndex === -1 || myIndex !== currentPlayer)) return;
      
      if (phase === "place") {
        const clamped = clampToSector(pos.x, pos.y);
        setPlacementPos(clamped);
        // Emit hover to server (throttled) so timeout can release from current cursor position
        if (ONLINE && socket) {
          const now = Date.now();
          if (now - hoverEmitRef.current > 100) {
            socket.emit('gameAction', { lobbyId, action: 'hover', payload: { position: clamped } });
            hoverEmitRef.current = now;
          }
        }
      } else if (phase === "aim" && aim && placementPos) {
        setAim((a) => (a ? { ...a, curr: pos } : a));
      }
    };

    const onDown = (e) => {
      if (gameState !== "playing" || currentPlayerConfig.isAI) return;
      
      if (phase === "roundEnd") {
        // During roundEnd, any player can click modal buttons (no turn restriction)
        // Handle modal button clicks
        const pos = getMousePos(e);
        const modalW = 420;
        const modalH = 380;
        const modalX = (W - modalW) / 2;
        const modalY = (H - modalH) / 2;
        const buttonY = modalY + modalH - 80;
        const buttonHeight = 35;
        const buttonGap = 10;
        
        if (ONLINE) {
          // Online game - single Return to Dashboard button
          const buttonW = 200;
          const startX = modalX + (modalW - buttonW) / 2;
          
          // Check Return to Dashboard button
          if (pos.x >= startX && pos.x <= startX + buttonW && 
              pos.y >= buttonY && pos.y <= buttonY + buttonHeight) {
            // Navigate to dashboard
            navigate('/dashboard');
            return;
          }
        } else {
          // Local game buttons
          const button1W = 100;
          const button2W = 120;
          const button3W = 100;
          const totalButtonW = button1W + button2W + button3W + (buttonGap * 2);
          const startX = modalX + (modalW - totalButtonW) / 2;
          
          // Check New Game button
          if (pos.x >= startX && pos.x <= startX + button1W && 
              pos.y >= buttonY && pos.y <= buttonY + buttonHeight) {
            setSeed((s) => (s + 1) >>> 0);
            return;
          }
          
          // Check Play Again button
          const button2X = startX + button1W + buttonGap;
          if (pos.x >= button2X && pos.x <= button2X + button2W && 
              pos.y >= buttonY && pos.y <= buttonY + buttonHeight) {
            // Reset with same seed for exact replay
            setSeed(seed); 
            setPucks([]); 
            setTurn(0); 
            setPhase("place"); 
            setSectorIdx([0,0,0,0]); 
            setPlacementPos(null); 
            setTimeLeft(MOVE_TIME_LIMIT); 
            setGameResultSaved(false);
            return;
          }
          
          // Check Back button
          const button3X = button2X + button2W + buttonGap;
          if (pos.x >= button3X && pos.x <= button3X + button3W && 
              pos.y >= buttonY && pos.y <= buttonY + buttonHeight) {
            if (isGuestMode) {
              window.history.back();
            } else {
              setGameState("lobby");
            }
            return;
          }
        }
        return;
      }
      
      // For regular gameplay, enforce turn restrictions
      if (ONLINE && (myIndex === -1 || myIndex !== currentPlayer)) return;
      
      const pos = getMousePos(e);
      if (phase === "place") {
        // FIXED: Always calculate placement from the EXACT click position
        // This ensures pixel-perfect placement matching where the user clicks
        const clamped = clampToSector(pos.x, pos.y);
        if (ONLINE && socket) {
          socket.emit('gameAction', { lobbyId, action: 'place', payload: { position: clamped } });
        } else {
          setPlacementPos(clamped);
          setPhase("aim");
        }
        return;
      }
      if (phase === "aim") {
        if (!placementPos) return;
        setAim({ start: { ...placementPos }, curr: pos });
      }
    };

    const onUp = (e) => {
      if (gameState !== "playing" || currentPlayerConfig.isAI) return;
      if (ONLINE && (myIndex === -1 || myIndex !== currentPlayer)) return;
      if (phase !== "aim" || !aim || !placementPos) return;
      const pos = getMousePos(e);
      const dx = pos.x - placementPos.x;
      const dy = pos.y - placementPos.y;
      const MAX_DRAG = 200, scale = 0.06;
      const mag = Math.min(MAX_DRAG, Math.hypot(dx, dy));
      const ang = Math.atan2(dy, dx);
      const vx = -(Math.cos(ang) * mag) * scale;
      const vy = -(Math.sin(ang) * mag) * scale;
      if (ONLINE && socket) {
        socket.emit('gameAction', { lobbyId, action: 'shoot', payload: { velocity: { vx, vy } } });
        setAim(null);
      } else {
        handleShot({ vx, vy });
      }
    };

    window.addEventListener("mousemove", onMove);
    canvas.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
    };
  }, [gameState, phase, placementPos, currentPlayer, aim, currentPlayerConfig.isAI, navigate]);

  // === Pointer Events for Mobile (touch-only) : pinch-zoom/pan + drag-to-aim/shoot ===
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.style.touchAction = 'none'; // allow custom gestures on mobile
    const getRect = () => canvas.getBoundingClientRect();
    const getPos = (e) => { const r = getRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };

    const pointers = new Map(); // id -> {x,y}
    let lastPinch = null;
    let aimingTouch = false;

    const onPointerDown = (e) => {
      if (e.pointerType !== 'touch') return; // don't interfere with mouse
      canvas.setPointerCapture(e.pointerId);
      const p = getPos(e);
      pointers.set(e.pointerId, p);

      // Only interact if it's my turn (online) or local human
      if (gameState !== 'playing' || currentPlayerConfig.isAI) return;
      if (ONLINE && (myIndex === -1 || myIndex !== currentPlayer)) return;

      if (pointers.size === 1) {
        const world = vpScreenToWorld(vpRef.current, p.x, p.y);
        if (phase === 'place') {
          const clamped = clampToSector(world.x, world.y);
          if (ONLINE && socket) {
            socket.emit('gameAction', { lobbyId, action: 'place', payload: { position: clamped } });
          } else {
            setPlacementPos(clamped);
            setPhase('aim');
          }
        } else if (phase === 'aim' && placementPos) {
          aimingTouch = true;
          setAim({ start: { ...placementPos }, curr: world });
        }
      } else if (pointers.size === 2) {
        lastPinch = null;
      }
    };

    const onPointerMove = (e) => {
      if (e.pointerType !== 'touch' || !pointers.has(e.pointerId)) return;
      const prev = pointers.get(e.pointerId);
      const cur = getPos(e);
      pointers.set(e.pointerId, cur);

      if (pointers.size === 1) {
        if (aimingTouch && phase === 'aim' && placementPos) {
          const world = vpScreenToWorld(vpRef.current, cur.x, cur.y);
          setAim(prevAim => prevAim ? ({ ...prevAim, curr: world }) : prevAim);
        }
      } else if (pointers.size === 2) {
        // pinch + pan
        const vals = [...pointers.values()];
        const d = Math.hypot(vals[1].x - vals[0].x, vals[1].y - vals[0].y);
        if (lastPinch) {
          const scaleDelta = d / lastPinch;
          const cx = (vals[0].x + vals[1].x) / 2;
          const cy = (vals[0].y + vals[1].y) / 2;
          vpApplyPinch(vpRef.current, scaleDelta, cx, cy);
          // pan by average movement
          const dx = (cur.x - prev.x);
          const dy = (cur.y - prev.y);
          vpRef.current.tx += dx;
          vpRef.current.ty += dy;
        }
        lastPinch = d;
      }
    };

    const onPointerUp = (e) => {
      if (e.pointerType !== 'touch' || !pointers.has(e.pointerId)) return;
      const pos = getPos(e);
      const world = vpScreenToWorld(vpRef.current, pos.x, pos.y);

      if (gameState === 'playing' && !currentPlayerConfig.isAI &&
          (!ONLINE || (myIndex !== -1 && myIndex === currentPlayer))) {
        if (phase === 'aim' && aimingTouch && placementPos && aim) {
          const dx = world.x - placementPos.x;
          const dy = world.y - placementPos.y;
          const MAX_DRAG = 200, scale = 0.06;
          const mag = Math.min(MAX_DRAG, Math.hypot(dx, dy));
          const ang = Math.atan2(dy, dx);
          const vx = -(Math.cos(ang) * mag) * scale;
          const vy = -(Math.sin(ang) * mag) * scale;
          if (ONLINE && socket) {
            socket.emit('gameAction', { lobbyId, action: 'shoot', payload: { velocity: { vx, vy } } });
            setAim(null);
          } else {
            handleShot({ vx, vy });
          }
        }
      }

      pointers.delete(e.pointerId);
      if (pointers.size < 2) lastPinch = null;
      aimingTouch = false;
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
    };
  }, [ONLINE, socket, lobbyId, gameState, phase, placementPos, currentPlayer, aim, currentPlayerConfig.isAI]);

  // UI
  return (
    <PageBG src="/bg/frozzle_me_plenty.jpg">
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        padding: '24px',
        backgroundColor: 'transparent',
        minHeight: '100vh',
        color: 'white'
      }}>
      {/* Title Section */}
      <div style={{
        textAlign: 'center',
        width: '100%',
        maxWidth: '1200px'
      }}>
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 800,
          background: 'linear-gradient(45deg, #60a5fa, #a855f7)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          margin: '0 0 1rem 0',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          Frozzlers - Online Multiplayer
        </h1>
        
        {/* Donation Section */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: '0.75rem',
          padding: '1rem 1.5rem',
          border: '2px solid rgba(96, 165, 250, 0.2)',
          marginBottom: '1rem',
          width: '800px',
          maxWidth: '100%',
          margin: '0 auto 1rem auto'
        }}>
          <p style={{
            fontSize: '0.95rem',
            color: '#e5e7eb',
            marginBottom: '0.75rem',
            lineHeight: 1.5
          }}>
            Created with ❤️ by a solo developer • Hundreds of hours of work • Forever FREE TO PLAY
          </p>
          <p style={{
            fontSize: '0.9rem',
            color: '#94a3b8',
            marginBottom: '1rem'
          }}>
            If you're enjoying Frozzlers, please consider supporting its continued development:
          </p>
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a
              href="https://paypal.me/DavidMielcarek541"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '0.5rem 1.5rem',
                background: 'linear-gradient(45deg, #0070ba, #003087)',
                color: 'white',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                transition: 'all 0.3s ease',
                display: 'inline-block'
              }}
              onMouseEnter={e => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 112, 186, 0.5)';
              }}
              onMouseLeave={e => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              💙 PayPal
            </a>
            
            <a
              href="https://buy.stripe.com/7sY9AL0IH6904cC5W67EQ00"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '0.5rem 1.5rem',
                background: 'linear-gradient(45deg, #635bff, #4338ca)',
                color: 'white',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                transition: 'all 0.3s ease',
                display: 'inline-block'
              }}
              onMouseEnter={e => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(99, 91, 255, 0.5)';
              }}
              onMouseLeave={e => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              💜 Stripe
            </a>
          </div>
        </div>
      </div>

      {(!ONLINE && gameState === "lobby" && !isGuestMode) ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          padding: '32px',
          backgroundColor: 'rgba(31, 41, 55, 0.8)',
          borderRadius: '16px',
          border: '1px solid rgba(75, 85, 99, 0.5)',
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '18px', fontWeight: '500', color: 'white', display: 'flex', alignItems: 'center', gap: '12px' }}>
              Players:
              <select 
                style={{
                  marginLeft: '8px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: 'white',
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                value={numPlayers} 
                onChange={(e) => setNumPlayers(Number(e.target.value))}
              >
                {[2, 3, 4].map((n) => (<option key={n} value={n}>{n}</option>))}
              </select>
            </label>
            <label style={{ fontSize: '18px', fontWeight: '500', color: 'white', display: 'flex', alignItems: 'center', gap: '12px' }}>
              Pucks each:
              <select 
                style={{
                  marginLeft: '8px',
                  backgroundColor: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: 'white',
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                value={pucksPerPlayer} 
                onChange={(e) => setPucksPerPlayer(Number(e.target.value))}
              >
                {[3, 5, 7].map((n) => (<option key={n} value={n}>{n}</option>))}
              </select>
            </label>
          </div>
          <ModernButton variant="primary" onClick={startGame}>
            Start Game
          </ModernButton>
        </div>
      ) : null}

      <canvas ref={canvasRef} width={W} height={H} style={{ touchAction: "none" }} />

      {/* Abandon Game Button - Only show during active game, hide during round end */}
      {gameState === "playing" && phase !== "roundEnd" && (
        <div style={{
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          <button
            onClick={() => {
              if (confirm('Are you sure you want to abandon this game?')) {
                if (ONLINE) {
                  // For online games, navigate to dashboard
                  navigate('/dashboard');
                } else {
                  // For local games, just reset to setup
                  setGameState("setup");
                  setPucks([]);
                  setTurn(0);
                  setPhase("place");
                  setPlacementPos(null);
                  setTimerActive(false);
                }
              }
            }}
            style={{
              background: 'linear-gradient(45deg, #dc2626, #b91c1c)',
              color: 'white',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 600,
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              marginBottom: '0.5rem'
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.5)';
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Abandon Game / Exit to Dashboard
          </button>
          <p style={{
            fontSize: '0.85rem',
            color: '#ef4444',
            fontStyle: 'italic',
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            Warning: Abandoning a ranked game will count as a loss and negatively impact your rating.
          </p>
        </div>
      )}

      {/* Instructions */}
      <div style={{ color: 'white', maxWidth: '800px', textAlign: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ fontSize: '18px', color: '#d1d5db', margin: 0 }}>
            <span style={{ color: '#60a5fa', fontWeight: '600' }}>Click</span> to place your puck in the highlighted sector, then <span style={{ color: '#60a5fa', fontWeight: '600' }}>drag and release</span> to shoot.
          </p>
          <p style={{ fontSize: '16px', color: '#9ca3af', margin: 0 }}>
            Score points by landing in the rings: <span style={{ color: '#f87171' }}>10</span>, <span style={{ color: '#60a5fa' }}>5</span>, <span style={{ color: '#34d399' }}>3</span>, <span style={{ color: '#fbbf24' }}>1</span>. 
            Hit the center dot for <span style={{ color: '#ffffff', fontWeight: '600' }}>15 points</span>!
          </p>
        </div>
      </div>

      {/* Mobile Controls */}
      {isMobileLike() && (
        <MobileControlsEnhanced
        ONLINE={ONLINE}
        socket={socket}
        lobbyId={lobbyId}
        gameState={gameState}
        phase={phase}
        currentPlayer={currentPlayer}
        currentPlayerConfig={currentPlayerConfig}
        placementPos={placementPos}
        setPlacementPos={setPlacementPos}
        setPhase={setPhase}
        setAim={setAim}
        handleShot={handleShot}
        clampToSector={clampToSector}
        ARENA={ARENA}
        myIndex={myIndex}
        currentSectorGeom={currentSectorGeom}
        playerColor={playerColor}
      />
      )}
      </div>
    </PageBG>
  );
}
